/**
 * Pure currency-planning helpers shared by the Organisation page and the
 * Currencies card. Keeping them here means the auto-promote rule lives in
 * exactly one place — the page can compute the planned state for its preview
 * memo and re-derive the same result inside `handleSubmit` without two
 * subtly-different copies drifting apart.
 *
 * No React, no hooks, no toasts — just data → data.
 */

/**
 * Parse the comma-separated `additionalCurrencyIsoCodes` field into a clean,
 * uppercased, deduped array, with the supplied `excludeBase` always removed.
 * Tolerant of whitespace and empty entries — never throws.
 *
 * The BE serialises the additional-currency list as a single comma-joined
 * string column (legacy shape carried forward from My.Tms.Old). The base
 * currency is stored separately, but historic rows occasionally have it
 * doubled-up in the additionals list — `excludeBase` strips that defensively
 * so the FE never renders the base as both "BASE" and "additional".
 */
export function parseAdditionalCurrencyCodes(
  raw: string | null | undefined,
  excludeBase: string,
): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  const upperBase = excludeBase.toUpperCase();
  for (const part of raw.split(',')) {
    const code = part.trim().toUpperCase();
    if (!code) continue;
    if (code === upperBase) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/**
 * Inputs for {@link planAdditionalCurrencies}. Bank accounts are typed
 * structurally so callers can pass whatever shape they have without forcing
 * an import of the full DTO type.
 */
export interface PlanAdditionalCurrenciesInput {
  /** Base currency currently persisted on the org (server-side truth). */
  serverBase: string | null | undefined;
  /** Base currency selected in the org form (what would land if we saved). */
  formBase: string;
  /** Raw `additionalCurrencyIsoCodes` string from the server payload. */
  serverAdditionalsRaw: string | null | undefined;
  /** Bank accounts attached to this org — drives the auto-promote check. */
  bankAccounts: ReadonlyArray<{ currencyIsoCode: string }>;
}

/**
 * Compute what the additionals list should be after the org form is saved,
 * given a (potentially edited) base currency.
 *
 * Auto-promote rule: when the form base differs from the server base, the
 * server base is added to additionals ONLY if some bank account is still
 * using it (preventing orphan rows whose currency is no longer enabled). If
 * nothing depends on the old base it's dropped silently — a clean swap, no
 * leftover currency the operator has to clean up later.
 *
 * The new base is always removed from the additionals (a base currency must
 * never appear in both slots).
 *
 * Used by:
 *  - the Organisation page's `effectiveAdditionalCodes` memo, which feeds the
 *    Currencies card preview and the Bank Accounts dropdown — operators see
 *    the planned state before clicking Save.
 *  - the page's `handleSubmit` flow, which fires the dedicated
 *    `PUT /organisations/{id}/additional-currencies` follow-up when the base
 *    changes. The page still compares this output against the parsed-current
 *    list to skip no-op round-trips.
 */
export function planAdditionalCurrencies(input: PlanAdditionalCurrenciesInput): string[] {
  const serverBase = (input.serverBase ?? '').toUpperCase();
  const formBase = input.formBase.trim().toUpperCase();
  const current = parseAdditionalCurrencyCodes(input.serverAdditionalsRaw, serverBase);

  // No (meaningful) base change → additionals stay exactly as they are.
  // Returning `current` directly (rather than round-tripping through a Set)
  // preserves the server's original order — handy for the preview UI.
  if (!serverBase || !formBase || serverBase === formBase) return current;

  const oldBaseInUse = input.bankAccounts.some(
    (b) => b.currencyIsoCode.toUpperCase() === serverBase,
  );
  const next = new Set(current);
  if (oldBaseInUse) next.add(serverBase);
  next.delete(formBase);
  return Array.from(next);
}
